import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import _ from 'lodash'
import {IEvent, IEventIdentity} from '@/features/labeling'

type FetchStatus = 'pending' | 'failed' | 'done'

const predictionToIndex = (event: IEvent): string => timestampToIndex(event.start)
const timestampToIndex = (timestamp: number): string => Math.round(timestamp).toString()

export interface IFrameExtractorWrapperProps<T extends IEventIdentity = IEventIdentity> {
  disabled?: boolean
  videoUrl?: string | ((clip: T) => Promise<string | undefined>)
  predictions: T[]
  children?: ReactNode
  maxParallelLoads?: number // Maximum number of parallel video loads
  clearCacheOnMount?: boolean // Whether to clear the global cache when the component mounts
}

export interface IFrameExtractorContext {
  isThumbnailPending: (event: IEventIdentity) => boolean
  hasThumbnailFor: (event: IEventIdentity) => boolean
  getThumbnailFor: (event: IEventIdentity) => string | undefined
}

export const FrameExtractorContext = createContext<IFrameExtractorContext>({
  isThumbnailPending: () => {
    throw new Error('not implemented')
  },
  hasThumbnailFor: () => {
    throw new Error('not implemented')
  },
  getThumbnailFor: () => {
    throw new Error('not implemented')
  },
})

interface VideoElement {
  element: HTMLVideoElement
  busy: boolean
  timestamp?: number
}

function FrameExtractorWrapperComponent<T extends IEventIdentity = IEventIdentity>({
  disabled,
  videoUrl,
  predictions,
  children,
  maxParallelLoads = 6,
}: IFrameExtractorWrapperProps<T>) {
  // Instead of a single video ref, we'll manage multiple video elements
  const videoElementsRef = useRef<VideoElement[]>([])
  const videoContainerRef = useRef<HTMLDivElement>(null)
  const pendingFramesRef = useRef<number[]>([])
  const currentVideoUrlRef = useRef<string>('')
  const isProcessingRef = useRef<boolean>(false)

  const [itemStates, setItemStates] = useState<{[key: string]: FetchStatus}>({})
  const [extractedVideoFrames, setExtractedVideoFrames] = useState<{[key: string]: string}>({})

  const cleanupVideoElements = useCallback(() => {
    videoElementsRef.current?.forEach((videoEl) => {
      videoEl.element.remove()
    })

    videoElementsRef.current = []

    if (videoContainerRef.current) {
      videoContainerRef.current.innerHTML = ''
    }
  }, [])

  const createVideoElements = useCallback(() => {
    if (!videoContainerRef.current) return

    cleanupVideoElements()

    // Create multiple video elements for parallel processing
    for (let i = 0; i < maxParallelLoads; i++) {
      const videoElement = document.createElement('video')
      videoElement.style.display = 'none'
      videoElement.crossOrigin = 'anonymous'
      videoElement.autoplay = false

      videoElement.addEventListener('loadeddata', () => handleVideoLoaded(i))
      videoElement.addEventListener('seeked', (e) => handleCaptureFrame(e, i))
      videoElement.addEventListener('error', () => handleVideoError(i))

      videoContainerRef.current.appendChild(videoElement)

      videoElementsRef.current.push({
        element: videoElement,
        busy: false,
      })
    }
  }, [cleanupVideoElements, maxParallelLoads])

  // initialize the video elements
  useEffect(() => {
    if (disabled || !videoContainerRef.current) return

    createVideoElements()

    return cleanupVideoElements
  }, [disabled, maxParallelLoads, videoUrl])

  const framesToProcess = useMemo(
    () => predictions.map((c) => predictionToIndex(c)).filter((c) => !itemStates[c]),
    [predictions, itemStates]
  )

  useEffect(() => {
    if (framesToProcess.length === 0 || !currentVideoUrlRef.current) return

    // Mark all new predictions as pending, except those already in cache
    const newStates: {[key: string]: FetchStatus} = {}

    framesToProcess.forEach((key) => {
      newStates[key] = 'pending'
    })

    setItemStates((prev) => ({...prev, ...newStates}))
  }, [framesToProcess])

  // Core functionality
  const processFramesInParallel = useCallback(() => {
    // Remove duplicate logging to reduce noise
    if (pendingFramesRef.current.length === 0) return

    const availableElements = videoElementsRef.current.filter((el) => !el.busy)

    if (availableElements.length > 0 && pendingFramesRef.current.length > 0) {
      isProcessingRef.current = true
    }

    availableElements.forEach((videoEl) => {
      if (pendingFramesRef.current.length === 0) return

      const timestamp = pendingFramesRef.current.shift()
      if (timestamp !== undefined) {
        videoEl.busy = true
        videoEl.timestamp = timestamp
        videoEl.element.currentTime = timestamp
      }
    })
  }, [])

  const handleVideoError = useCallback((index: number) => {
    const videoEl = videoElementsRef.current[index]
    if (videoEl.timestamp) {
      const ts = timestampToIndex(videoEl.timestamp)
      setItemStates((prev) => ({...prev, [ts]: 'failed'}))
    }
    videoEl.timestamp = undefined
    videoEl.busy = false
    isProcessingRef.current = false
  }, [])

  const handleCreateFrame = useCallback((index: number, width: number, height: number) => {
    const videoEl = videoElementsRef.current[index]
    if (!videoEl || !videoEl.timestamp || !currentVideoUrlRef.current) return

    const newCanvas = document.createElement('canvas')
    newCanvas.width = width
    newCanvas.height = height
    const newCtx = newCanvas.getContext('2d')

    if (!newCtx) {
      console.error('Failed to get canvas context')
      return undefined
    }

    try {
      newCtx.drawImage(videoEl.element, 0, 0, width, height)
      const dataUrl = newCanvas.toDataURL()

      videoEl.busy = false
      videoEl.timestamp = undefined

      return dataUrl
    } catch (error) {
      console.error('Failed to create frame', error)
      return undefined
    }
  }, [])

  const handleVideoLoaded = useCallback(
    (index: number) => {
      if (videoElementsRef.current[index].timestamp !== undefined) {
        // If this video already has a timestamp assigned, seek to it
        videoElementsRef.current[index].element.currentTime =
          videoElementsRef.current[index].timestamp
      } else {
        videoElementsRef.current[index].busy = false
        processFramesInParallel()
      }
    },
    [processFramesInParallel]
  )

  const handleCaptureFrame = useCallback(
    (e: Event, index: number) => {
      const videoEl = videoElementsRef.current[index]
      if (!videoEl || !videoEl.timestamp || !currentVideoUrlRef.current) return

      const t = e.target as HTMLVideoElement
      const ts = timestampToIndex(videoEl.timestamp)
      const frame = handleCreateFrame(index, t.videoWidth, t.videoHeight)

      if (!frame) {
        setItemStates((prev) => ({
          ...prev,
          [ts]: 'failed',
        }))
        return
      }

      setExtractedVideoFrames((prev) => ({
        ...prev,
        [ts]: frame,
      }))

      setItemStates((prev) => ({
        ...prev,
        [ts]: 'done',
      }))

      processFramesInParallel()

      if (
        pendingFramesRef.current.length === 0 &&
        !predictions.some((p) => itemStates[predictionToIndex(p)] === 'pending')
      ) {
        isProcessingRef.current = false
      }
    },
    [processFramesInParallel, handleCreateFrame, predictions, itemStates]
  )

  const handleSetVideoUrl = useCallback(async () => {
    if (!videoUrl && !currentVideoUrlRef.current) return

    const firstClip = _.first(predictions)
    if (!firstClip) {
      isProcessingRef.current = false
      return
    }

    const _videoUrl = _.isFunction(videoUrl) ? await videoUrl(firstClip as T) : videoUrl
    if (!_videoUrl) {
      isProcessingRef.current = false
      if (currentVideoUrlRef.current) {
        currentVideoUrlRef.current = ''
        setExtractedVideoFrames({})
        setItemStates({})
      }
      return
    }

    if (currentVideoUrlRef.current === _videoUrl) return

    setExtractedVideoFrames({})
    setItemStates({})
    currentVideoUrlRef.current = _videoUrl
  }, [predictions, videoUrl])

  useEffect(() => {
    handleSetVideoUrl()
  }, [handleSetVideoUrl])

  // Combine getURL and processFrames into a single function
  const loadAndProcessFrames = useCallback(async () => {
    // Skip if all predictions are already processed
    const pendingClips = predictions?.filter(
      (c) => !c.url && itemStates[predictionToIndex(c)] === 'pending'
    )
    // Skip if disabled, already processing, or no predictions
    if (
      disabled ||
      isProcessingRef.current ||
      !pendingClips?.length ||
      !videoElementsRef.current.length
    ) {
      return
    }

    isProcessingRef.current = true

    const targetClips = pendingClips.filter((c) => {
      const key = predictionToIndex(c)
      // Skip if already processed
      return !(extractedVideoFrames[key] || itemStates[key] === 'done')
    })

    if (targetClips.length === 0) {
      isProcessingRef.current = false
      return
    }

    pendingFramesRef.current = targetClips.map((clip) => clip.start).filter(_.isNumber)

    try {
      // Load video in elements if needed
      videoElementsRef.current.forEach((videoEl) => {
        if (videoEl.element.src !== currentVideoUrlRef.current) {
          videoEl.element.src = currentVideoUrlRef.current
        }
        videoEl.busy = false
      })

      // Start processing - only call processFramesInParallel once here
      processFramesInParallel()
    } catch (error) {
      console.error('Error in loadAndProcessFrames:', error)
      isProcessingRef.current = false
    }
  }, [disabled, itemStates, extractedVideoFrames, processFramesInParallel])

  // Trigger the loading process whenever predictions or itemStates change
  useEffect(() => {
    loadAndProcessFrames()
  }, [loadAndProcessFrames])

  // Context-exposed functions
  const isThumbnailPending = useCallback(
    (event: IEvent): boolean => itemStates[predictionToIndex(event)] === 'pending',
    [itemStates]
  )

  const hasThumbnailFor = useCallback(
    (event: IEvent): boolean => _.has(extractedVideoFrames, predictionToIndex(event)),
    [extractedVideoFrames]
  )

  const getThumbnailFor = useCallback(
    (event: IEvent): string | undefined => extractedVideoFrames[predictionToIndex(event)],
    [extractedVideoFrames]
  )

  return (
    <FrameExtractorContext.Provider
      value={{
        isThumbnailPending,
        hasThumbnailFor,
        getThumbnailFor,
      }}
    >
      {!disabled ?
        <div
          ref={videoContainerRef}
          style={{display: 'none'}}
        />
      : undefined}
      {children}
    </FrameExtractorContext.Provider>
  )
}

// Memoized wrapper that preserves generic type parameters
export const FrameExtractorWrapper = React.memo(FrameExtractorWrapperComponent) as <
  T extends IEventIdentity = IEventIdentity,
>(
  props: IFrameExtractorWrapperProps<T> & {children?: ReactNode}
) => JSX.Element
